import React, {
  ReactNode,
  memo,
  useState,
  useCallback,
  PropsWithChildren,
} from "react";
import { ViewportElement } from "@foundation/viewport-element";

interface LazyLoadProps {
  /** Render this while the real children are “off-screen.” */
  placeholder: ReactNode;
  /** How early (in px) before entering the viewport to swap in children */
  rootMargin?: string;
  onEnterDelay?: number;
}

/**
 * LazyLoad
 *
 * Delays mounting `children` (and thus any data-fetch they kick off)
 * until the wrapper is within `rootMargin` of viewport.
 */
export const LazyLoad = memo(function LazyLoadComponent({
  children,
  placeholder,
  rootMargin = "200px",
  onEnterDelay = 250,
}: PropsWithChildren<LazyLoadProps>) {
  const [entered, setEntered] = useState(false);

  const onEnter = useCallback(() => {
    setTimeout(() => setEntered(true), onEnterDelay);
  }, [onEnterDelay]);

  return (
    <ViewportElement onEnter={onEnter} rootMargin={rootMargin}>
      {entered ? children : placeholder}
    </ViewportElement>
  );
});
